import React, { useEffect, useState, useContext } from "react";
import AppContainer from "../../Components/Structure/AppContainer";
import TableHeader from "../../Components/Common/TableHeader";
import Loader from "../../Components/Common/Loader";
import { toast, Toaster } from "react-hot-toast";
import ajaxProduct from "../../util/remote/ajaxProduct";
import Select from "react-select";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ajaxStores from "../../util/remote/ajaxStores";
import UpdateInventoryRecord from "./UpdateInventoryRecord";
import useStateCallback from "../../util/customHooks/useStateCallback";
import ClientContext from "../../Context/ClientContext"; // <-- import context
const ListNonTransferMovements = () => {
  const { clientList } = useContext(ClientContext); // <-- from context
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 40,
  });
  // Modal state (using useStateCallback pattern)
  const [update, setUpdate] = useStateCallback(false);
  // Load master data for dropdowns (products and stores only)
  useEffect(() => {
    const loadData = async () => {
      const productRes = await ajaxProduct.fetchProductList();
      if (productRes.status === "OK") setProducts(productRes.details);
      const storeRes = await ajaxStores.fetchStores();
      if (storeRes.status === "OK") setStores(storeRes.details);
    };
    loadData();
  }, []);
  const fetchMovements = async (page = 1) => {
    setLoading(true);
    const payload = {
      page: page,
      per_page: pagination.per_page,
      product_id: selectedProduct?.id || null,
      start_date: startDate || null,
      end_date: endDate || null,
    };
    const res = await ajaxProduct.listNonTransferMovements(payload);
    setLoading(false);
    if (res.status === "OK") {
      const { meta, list } = res.details;
      setMovements(list);
      setPagination({
        current_page: meta.current_page,
        total_pages: meta.total_pages,
        total_records: meta.count,
        per_page: meta.items_per_page,
      });
    } else {
      toast.error(res.message || "Failed to load movements");
      setMovements([]);
    }
  };
  useEffect(() => {
    fetchMovements(1);
  }, [selectedProduct, startDate, endDate]);
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.total_pages) return;
    fetchMovements(newPage);
  };
  const resetFilters = () => {
    setSelectedProduct(null);
    setStartDate("");
    setEndDate("");
  };
  // Helper to extract quantity value
  const getQuantityValue = (qtyObj) => {
    if (!qtyObj) return "";
    if (typeof qtyObj === "object") {
      return (
        qtyObj.quantity_in || qtyObj.quantity_out || qtyObj.quantity_in_c || ""
      );
    }
    return qtyObj;
  };
  // Delete handler
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    setLoading(true);
    const res = await ajaxProduct.deleteInventoryRecord({ id });
    setLoading(false);
    if (res.status === "OK") {
      toast.success("Record deleted successfully");
      fetchMovements(pagination.current_page);
    } else {
      toast.error(res.message || "Delete failed");
    }
  };
  // Open edit modal (using useStateCallback)
  const handleEditClick = (record) => {
    setUpdate(false, () =>
      setUpdate(
        <UpdateInventoryRecord
          data={record}
          function={() => {
            fetchMovements(pagination.current_page);
            setUpdate(false); // close modal after refresh
          }}
        />,
      ),
    );
  };
  const exportToPDF = () => {
    const table = document.querySelector(".movements-table");
    if (!table) return;
    const pdf = new jsPDF("p", "pt", "a4");
    const companyName = "BERICOT AFRICA LIMITED";
    const companyAddress = "KAMPALA UGANDA \n TEL:+256701666160";
    const currentDate = `Date of Printing: ${new Date().toLocaleDateString()}`;
    const headerDescription = "Non‑Transfer Inventory Movements Report";
    const pageWidth = pdf.internal.pageSize.getWidth();
    pdf.setFontSize(14);
    pdf.text(companyName, (pageWidth - pdf.getTextWidth(companyName)) / 2, 50);
    pdf.setFontSize(12);
    const addressLine1 = companyAddress.split("\n")[0];
    const addressLine2 = companyAddress.split("\n")[1];
    pdf.text(
      addressLine1,
      (pageWidth - pdf.getTextWidth(addressLine1)) / 2,
      80,
    );
    pdf.text(
      addressLine2,
      (pageWidth - pdf.getTextWidth(addressLine2)) / 2,
      100,
    );
    pdf.setFontSize(9);
    pdf.text(currentDate, (pageWidth - pdf.getTextWidth(currentDate)) / 2, 130);
    pdf.setFontSize(12);
    pdf.text(
      headerDescription,
      (pageWidth - pdf.getTextWidth(headerDescription)) / 2,
      160,
    );
    const columns = [
      "Date",
      "Product",
      "Qty In",
      "Qty Out",

      "Store",
      "Client",
      "Description",
    ];
    const rows = Array.from(table.querySelectorAll("tbody tr")).map((row) => {
      const cells = row.querySelectorAll("td");
      return [
        cells[0]?.innerText || "",
        cells[1]?.innerText || "",
        cells[2]?.innerText || "",
        cells[3]?.innerText || "",
        cells[4]?.innerText || "",
        cells[5]?.innerText || "",
        cells[6]?.innerText || "",
      ];
    });
    pdf.autoTable({ startY: 180, head: [columns], body: rows });
    pdf.save(
      `inventory_movements_${new Date().toISOString().slice(0, 10)}.pdf`,
    );
  };
  const getPageNumbers = () => {
    let pages = [];
    const maxVisible = 5;
    let start = Math.max(
      1,
      pagination.current_page - Math.floor(maxVisible / 2),
    );
    let end = Math.min(pagination.total_pages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  return (
    <AppContainer title="Inventory Movements (Non‑Transfer)">
      <Toaster />
      {/* Render modal dynamically */}
      {update}
      {/* Filters Card */}
      <div className="card mb-2">
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <label>Product</label>
              <Select
                options={products}
                getOptionLabel={(opt) => opt.product_name}
                getOptionValue={(opt) => opt.id}
                value={selectedProduct}
                onChange={setSelectedProduct}
                isClearable
                placeholder="All products"
              />
            </div>
            <div className="col-md-3">
              <label>From Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label>To Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-secondary" onClick={resetFilters}>
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Data Table */}
      <div className="card custom-card">
        <div className="card-body">
          {loading && <Loader />}
          <TableHeader
            title="Inventory Movements"
            subtitle="All stock movements that are not part of store transfers"
            viewButton={
              <button
                onClick={exportToPDF}
                className="btn btn-secondary btn-sm"
                style={{ float: "right", borderRadius: "5px" }}
              >
                Export to PDF
              </button>
            }
          />
          <div className="table-responsive">
            <table className="table table-striped table-bordered movements-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Qty In</th>
                  <th>Qty Out</th>
                  <th>Store</th>
                  <th>Client</th>
                  <th>Description</th>
                  <th>Added By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center">
                      No movements found
                    </td>
                  </tr>
                )}
                {movements.map((mov) => (
                  <tr key={mov.id}>
                    <td>
                      {mov.date_added?.short_date || mov.date_added || ""}
                    </td>
                    <td>
                      {mov.product_name} ({mov.unit_measure})
                    </td>
                    <td>{getQuantityValue(mov.quantity_in)}</td>
                    <td>{getQuantityValue(mov.quantity_out)}</td>
                    <td>{mov.store_name}</td>
                    <td>{mov.client_name}</td>
                    <td>{mov.description}</td>
                    <td>{mov.added_by}</td>
                    <td>
                      <button
                        onClick={() => handleEditClick(mov)}
                        className="btn btn-sm btn-warning me-1"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(mov.id)}
                        className="btn btn-sm btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.total_pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <button
                className="btn btn-sm btn-primary"
                disabled={pagination.current_page <= 1}
                onClick={() => handlePageChange(pagination.current_page - 1)}
              >
                Previous
              </button>
              <div>
                {getPageNumbers().map((p) => (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`btn btn-sm mx-1 ${p === pagination.current_page ? "btn-primary" : "btn-outline-primary"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                className="btn btn-sm btn-primary"
                disabled={pagination.current_page >= pagination.total_pages}
                onClick={() => handlePageChange(pagination.current_page + 1)}
              >
                Next
              </button>
            </div>
          )}
          <div className="text-muted mt-2 small">
            Showing {movements.length} of {pagination.total_records} records
          </div>
        </div>
      </div>
    </AppContainer>
  );
};
export default ListNonTransferMovements;
